'use client';

import { useLayoutEffect, useSyncExternalStore } from 'react';

import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { useOptionalAuth } from '@/components/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Link } from '@/i18n/routing';
import { normalizeUserRole } from '@/lib/auth/roles';
import {
  getServerViewerSnapshot,
  readViewerCookieSnapshot,
  subscribeViewerCookie,
  viewerInitials,
  type ViewerSnapshot,
} from '@/lib/auth/viewer-cookie';
import { getRedirectPath } from '@/lib/auth-redirect';
import { cn } from '@/lib/utils';
import type { UserProfile, UserRole } from '@/types';

export interface AccountMenuProps {
  variant: 'header' | 'drawer';
  onNavigate?: () => void;
}

const headerActionClass =
  'rounded-lg px-4 !h-9 !text-sm font-medium tracking-[0.02em] !leading-none whitespace-nowrap !mb-0 border border-oxford-blue/20 bg-transparent text-oxford-blue/90 hover:border-oxford-blue/40 hover:bg-oxford-blue/[0.04] hover:text-oxford-blue focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-offset-2 focus-visible:ring-offset-white';

const drawerActionClass =
  'h-11 w-full rounded-lg border border-oxford-blue/20 bg-transparent text-sm font-medium tracking-[0.02em] text-oxford-blue/90 hover:border-oxford-blue/40 hover:bg-oxford-blue/[0.04] hover:text-oxford-blue focus-visible:ring-2 focus-visible:ring-orange-peel focus-visible:ring-offset-2 focus-visible:ring-offset-white';

function subscribeHydration(): () => void {
  return () => {};
}

function getHydratedSnapshot(): boolean {
  return true;
}

function getServerHydratedSnapshot(): boolean {
  return false;
}

function useHeaderViewer(): { viewer: ViewerSnapshot | null; pending: boolean } {
  const cookieViewer = useSyncExternalStore(
    subscribeViewerCookie,
    readViewerCookieSnapshot,
    getServerViewerSnapshot,
  );
  const hydrated = useSyncExternalStore(
    subscribeHydration,
    getHydratedSnapshot,
    getServerHydratedSnapshot,
  );
  const auth = useOptionalAuth();

  if (auth && !auth.loading) {
    if (!auth.user) {
      return { viewer: null, pending: false };
    }
    return {
      viewer: {
        name: auth.userProfile?.full_name?.trim() ?? '',
        role: normalizeUserRole(auth.userProfile?.role ?? null),
      },
      pending: false,
    };
  }

  if (!hydrated) {
    return { viewer: null, pending: true };
  }

  return { viewer: cookieViewer, pending: false };
}

function dashboardHref(role: UserRole): string {
  return getRedirectPath({ role } as UserProfile);
}

function InitialsBubble({ initials }: { initials: string }) {
  return (
    <span
      aria-hidden='true'
      className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-oxford-blue text-[10px] font-semibold leading-none text-white'
    >
      {initials}
    </span>
  );
}

export function AccountMenu({ variant, onNavigate }: AccountMenuProps) {
  const t = useTranslations('common.nav');
  const { viewer, pending } = useHeaderViewer();

  useLayoutEffect(() => {
    if (pending) {
      if (readViewerCookieSnapshot()) {
        document.documentElement.setAttribute('data-signed-in', '');
      }
      return;
    }
    if (viewer) {
      document.documentElement.setAttribute('data-signed-in', '');
    } else {
      document.documentElement.removeAttribute('data-signed-in');
    }
  }, [pending, viewer]);

  const showSignedOut = pending || !viewer;
  const showSignedIn = pending || Boolean(viewer);
  const actionClass = variant === 'header' ? headerActionClass : drawerActionClass;

  return (
    <>
      {showSignedOut ? (
        <Button
          asChild
          size={variant === 'header' ? 'sm' : 'default'}
          variant='outline'
          className={cn(actionClass, pending && 'account-signed-out-slot')}
        >
          <Link href='/auth/signup' onClick={onNavigate}>
            {t('signIn')}
          </Link>
        </Button>
      ) : null}
      {showSignedIn ? (
        viewer ? (
          <SignedInAccount
            variant={variant}
            viewer={viewer}
            onNavigate={onNavigate}
            actionClass={actionClass}
          />
        ) : (
          <button
            type='button'
            className={cn(
              actionClass,
              'inline-flex items-center gap-2',
              pending && 'account-signed-in-slot',
            )}
            aria-label={t('account')}
          >
            <InitialsBubble initials={Array.from(t('account'))[0] ?? ''} />
            <span className='truncate'>{t('account')}</span>
          </button>
        )
      ) : null}
    </>
  );
}

interface SignedInAccountProps {
  variant: 'header' | 'drawer';
  viewer: ViewerSnapshot;
  onNavigate?: () => void;
  actionClass: string;
}

function SignedInAccount({ variant, viewer, onNavigate, actionClass }: SignedInAccountProps) {
  const t = useTranslations('common.nav');
  const displayName = viewer.name || t('account');
  const roleText =
    viewer.role === 'admin'
      ? t('roleAdmin')
      : viewer.role === 'provider'
        ? t('rolePartner')
        : t('roleCustomer');
  const initials = viewerInitials(viewer.name) || (Array.from(displayName)[0] ?? '');
  const href = dashboardHref(viewer.role);
  const accessibleName = t('signedInAs', { name: displayName, role: roleText });

  if (variant === 'drawer') {
    return (
      <div className='flex flex-col gap-2.5'>
        <div className='flex min-w-0 items-center gap-3 px-1'>
          <InitialsBubble initials={initials} />
          <div className='min-w-0'>
            <p className='truncate text-sm font-semibold text-oxford-blue'>{displayName}</p>
            <p className='truncate text-sm text-oxford-blue'>{roleText}</p>
          </div>
        </div>
        <Button asChild variant='outline' className={actionClass}>
          <Link href={href} onClick={onNavigate}>
            {t('dashboard')}
          </Link>
        </Button>
        <Button asChild variant='outline' className={actionClass}>
          <Link href='/logout' onClick={onNavigate}>
            {t('signOut')}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type='button'
          size='sm'
          variant='outline'
          className={cn(actionClass, 'inline-flex max-w-[16rem] items-center gap-2')}
          aria-label={accessibleName}
        >
          <InitialsBubble initials={initials} />
          <span className='truncate'>{displayName}</span>
          <span aria-hidden='true'>·</span>
          <span className='truncate'>{roleText}</span>
          <ChevronDown className='h-3.5 w-3.5 shrink-0' aria-hidden='true' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='z-[220] min-w-[14rem] bg-white text-oxford-blue'>
        <DropdownMenuItem asChild className='min-h-11 cursor-pointer'>
          <Link href={href} onClick={onNavigate}>
            {t('dashboard')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className='min-h-11 cursor-pointer'>
          <Link href='/logout' onClick={onNavigate}>
            {t('signOut')}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
